
import IconWithDetail from "@/components/objects/public/attrs/icons/items/IconWithDetail.vue";
import { AttrsList, DesignExtra } from "@/utils/dto/objects.js";
import { adjustColorBrightness } from "@/utils/helpers.js";
import TheBlockHeader from "@/components/TheBlockHeader.vue";

function hexToRgb(hex) {
  var r = parseInt(hex.slice(1, 3), 16);
  var g = parseInt(hex.slice(3, 5), 16);
  var b = parseInt(hex.slice(5, 7), 16);
  return { r: r, g: g, b: b };
}

function rgbToHex(r, g, b) {
  var hexR = r.toString(16).padStart(2, '0');
  var hexG = g.toString(16).padStart(2, '0');
  var hexB = b.toString(16).padStart(2, '0');
  return '#' + hexR + hexG + hexB;
}

export default {
  name: "IconAttrWithDetail",
  components: { TheBlockHeader, IconWithDetail },
  inject: {
    specialProject: {
      default: false
    }
  },
  props: {
    items: {
      type: [Array, AttrsList],
      default: () => {
        return []
      }
    },
    objectId: {
      type: String
    },
    listInfo: {
      type: Object
    },
    useStorage: {
      type: Boolean,
      default: false
    },
    design: {
      type: String,
    },
    template: {
      type: Object,
    },
    user: {
      type: Object,
      default: () => {}
    },
    design_extra: {
      type: [Object, DesignExtra],
      default: () => {}
    },
  },
  data() {
    return {
      detail: false,
      isMobile: false,
    }
  },
  computed: {
    colors() {
      if (this.getBgGradientEnabled) {
        if (this.design_extra?.bg?.color) {
          return this.generateColorTransition(adjustColorBrightness(this.getBgColor(), 1.20) ?? '#EBF8FF', this.getBgColor(), this.items.length);
        } else if (this.bgPalette) {
          return [this.bgPalette.main_color, ...this.bgPalette.colors]
        }
      } else {
        if (this.design_extra?.bg?.color) {
          return this.generateColorTransition(this.getAdjustMainColor, this.getMainColor, this.items.length)
        } else if (this.bgPalette) {
          return [this.bgPalette.main_color, ...this.bgPalette.colors]
        }
      }

      return this.generateColorTransition("#EBF8FF", "#D7F1FF", this.items.length)
    },
    even() {
      return this.items.length % 2 === 0
    },
    getBlockName() {
      return this.getImageType ? 'links' + '_' + this.getImageType : 'links'
    },
    getImageType() {
      return this.items[0]?.image_type === 'image' ? 'images' : 'icons'
    },
    paletteList() {
      return this.$store.state["property_templates"]["consts"].palette;
    },
    bgPalette() {
      if (this.design_extra?.bg?.palette && this.paletteList) {
        return this.paletteList.filter(el => el.id === this.design_extra.bg.palette)[0];
      }

      return null;
    },
    iconBgPalette() {
      if (this.design_extra?.icon_bg?.palette && this.paletteList) {
        return this.paletteList.filter(el => el.id === this.design_extra.icon_bg.palette)[0];
      }

      return null;
    },
    getTextColor() {
      return this.design_extra?.text?.color ?? null;
    },
    getMask() {
      return this.design_extra?.mask?.name ?? null;
    },
    getMainColor() {
      if (this.getBgGradientEnabled) {
        return `linear-gradient(to ${this.getBgGradientDirection}, ${this.getBgGradientShadowDirection === 'transparent' ? `${this.getBgColor(0)}, transparent` : `${this.getBgColor(0)}, ${adjustColorBrightness(this.getBgColor(0), 1.20)}`})`
      }
      return this.getBgColor(0) ?? '#D7F1FF';
    },
    getAdjustMainColor() {
      if (this.getBgGradientEnabled) {
        return `linear-gradient(to ${this.getBgGradientDirection}, ${this.getBgGradientShadowDirection === 'transparent' ? `${adjustColorBrightness(this.getBgColor(0), 1.20)}, transparent` : `${adjustColorBrightness(this.getBgColor(0), 1.20)}, ${adjustColorBrightness(this.getBgColor(0), 1.40)}`})`;
      }
      if (this.getBgColor(0)) {
        return adjustColorBrightness(this.getMainColor, 1.20);
      }

      return '#EBF8FF';
    },
    getFirstBlockBg() {
      return this.design_extra?.first_block?.bg_color ?? this.getBgColor(0) ?? null;
    },
    getFirstBlockBgGradientEnabled() {
      if (this.hasNotFirstBlockBgAndHasBgPalette) {
        return this.design_extra?.bg?.gradient_enabled ?? null;
      } else {
        return this.design_extra?.first_block?.gradient_enabled ?? null;
      }
    },
    getFirstBlockBgGradientDirection() {
      if (this.hasNotFirstBlockBgAndHasBgPalette) {
        return this.design_extra?.bg?.gradient_direction ?? null;
      } else {
        return this.design_extra?.first_block?.gradient_direction ?? null;
      }
    },
    getFirstBlockBgGradientShadowDirection() {
      if (this.hasNotFirstBlockBgAndHasBgPalette) {
        return this.design_extra?.bg?.gradient_shadow_direction ?? null;
      } else {
        return this.design_extra?.first_block?.gradient_shadow_direction ?? null;
      }
    },
    getFirstBlockTitleColor() {
      return this.design_extra?.first_block?.title_color ?? this.getBgColor(0) ?? '#D7F1FF' ?? null;
    },
    getBgGradientEnabled() {
      return this.design_extra?.bg?.gradient_enabled ?? null;
    },
    getBgGradientDirection() {
      return this.design_extra?.bg?.gradient_direction ?? null;
    },
    getBgGradientShadowDirection() {
      return this.design_extra?.bg?.gradient_shadow_direction ?? null;
    },
    getIconGradientEnabled() {
      return this.design_extra?.icon_bg?.gradient_enabled ?? null;
    },
    getIconGradientDirection() {
      return this.design_extra?.icon_bg?.gradient_direction ?? null;
    },
    getIconGradientShadowDirection() {
      return this.design_extra?.icon_bg?.gradient_shadow_direction ?? null;
    },
    hasNotFirstBlockBgAndHasBgPalette() {
      if (!this.design_extra?.first_block?.bg_color && this.bgPalette) {
        return true;
      } else return false;
    },
    blockBorderPalette() {
      if (this.design_extra?.block_border?.palette && this.paletteList) {
        return this.paletteList.filter(el => el.id === this.design_extra.block_border.palette)[0];
      }

      return null;
    },
  },
  mounted() {
    window.addEventListener('resize', this.resizeHandler, { passive: true })
  },
  created() {
    if (process.client) {
      this.resizeHandler()
    }
  },
  destroyed() {
    window.removeEventListener('resize', this.resizeHandler)
  },
  methods: {
    getBlockBorderColor(index) {
      if (this.blockBorderPalette) {
        return [this.blockBorderPalette.main_color, ...this.blockBorderPalette.colors][index % [this.blockBorderPalette.main_color, ...this.blockBorderPalette.colors].length];
      } else if (this.design_extra?.block_border?.color) {
        return this.design_extra.block_border.color
      }

      return null;
    },
    getBgColor(index) {
      if (this.bgPalette) {
        return [this.bgPalette.main_color, ...this.bgPalette.colors][index % [this.bgPalette.main_color, ...this.bgPalette.colors].length];
      } else if (this.design_extra?.bg?.color) {
        return this.design_extra.bg.color;
      }

      return 'transparent';
    },
    getIconBgColor(index) {
      if (this.getIconGradientEnabled) {
        if (this.iconBgPalette) {
          const color = [this.iconBgPalette.main_color, ...this.iconBgPalette.colors][index % [this.iconBgPalette.main_color, ...this.iconBgPalette.colors].length];
          return `linear-gradient(to ${this.getIconGradientDirection}, ${this.getIconGradientShadowDirection === 'transparent' ? `${color}, transparent` : `${color}, ${adjustColorBrightness(color, 1.20)}`})`
        } else if (this.design_extra?.icon_bg?.color) {
          const color = this.design_extra.icon_bg.color;
          return `linear-gradient(to ${this.getIconGradientDirection}, ${this.getIconGradientShadowDirection === 'transparent' ? `${color}, transparent` : `${color}, ${adjustColorBrightness(color, 1.20)}`})`
        }
      } else {
        if (this.iconBgPalette) {
          return [this.iconBgPalette.main_color, ...this.iconBgPalette.colors][index % [this.iconBgPalette.main_color, ...this.iconBgPalette.colors].length];
        } else if (this.design_extra?.icon_bg?.color) {
          return this.design_extra.icon_bg.color;
        }
      }

      return null;
    },
    getIconColor(index) {
      return this.design_extra?.icon_color?.color ?? (this.$route.hash === `#${this.items[index].id}` ? 'white' : 'vistat');
    },
    generateColorTransition(startColor, endColor, steps) {
      var start = hexToRgb(startColor);
      var end = hexToRgb(endColor);

      var stepR = (end.r - start.r) / (steps - 1);
      var stepG = (end.g - start.g) / (steps - 1);
      var stepB = (end.b - start.b) / (steps - 1);

      var colors = [];
      for (var i = 0; i < steps; i++) {
        var r = Math.round(start.r + stepR * i);
        var g = Math.round(start.g + stepG * i);
        var b = Math.round(start.b + stepB * i);
        var color = rgbToHex(r, g, b);
        colors.push(color);
      }

      return colors;
    },
    resizeHandler() {
      this.isMobile = Boolean(window.innerWidth < 640)
    }
  }
}
